import * as offerSessionUtils from '../offerSessionUtils/offerSessionUtils';
import { importModule } from './moduleImport';

declare const __webpack_init_sharing__: (scope: 'default') => Promise<void>;
declare const __webpack_share_scopes__: { default: unknown };

/**
 * Local New Relic logger — small wrapper over `window.newrelic.addPageAction`.
 *
 * Why not just `import { logNewRelicPageAction } from '@makemydeal/dr-activities-common'`?
 *
 *   `shared-ui-utils` is a low-level utility package with NO React/Redux
 *   dependencies, on purpose. `dr-activities-common` sits above us and
 *   bundles React/Redux-connected components. Importing from it here would:
 *     • create a circular dependency (it already depends on us),
 *     • pull React + Redux into every consumer's bundle, and
 *     • break unrelated tests that mock `react-redux`.
 *
 *   The function we'd be importing is just a few lines around the
 *   `window.newrelic` global — no business logic. So we keep those few
 *   lines local and stay dependency-free.
 */
type NewRelicGlobal = {
    addPageAction?: (name: string, attributes?: Record<string, unknown>) => void;
};

const logNewRelicPageAction = (name: string, attributes?: Record<string, unknown>): void => {
    /* istanbul ignore next -- defensive guard for SSR; not exercised in jsdom tests */
    if (typeof window === 'undefined') return;

    const nr = (window as unknown as { newrelic?: NewRelicGlobal }).newrelic;

    if (nr && typeof nr.addPageAction === 'function') {
        nr.addPageAction(name, attributes);
    }
};

/**
 * Stable New Relic event names emitted by the module federation loader.
 * Keep these names stable — they are referenced by NRQL queries on the
 * "MV — Common Consumer Card Integration Health" dashboard (awsdrst account).
 */
export const MODULE_FEDERATION_NR_EVENTS = {
    MANIFEST_LOAD_FAILURE: 'MV:CCC:ManifestLoadFailure',
    REMOTE_CONTAINER_LOAD_FAILURE: 'MV:CCC:RemoteContainerLoadFailure',
    MODULE_LOAD_FAILURE: 'MV:CCC:ModuleLoadFailure',
    MODULE_LOAD_SUCCESS: 'MV:CCC:ModuleLoadSuccess'
} as const;

let remoteUrlDefinitions: Record<string, string>;

export function setRemoteDefinitions(definitions: Record<string, string>) {
    remoteUrlDefinitions = definitions;
}

export const getRemoteUrlDefinitions = () => remoteUrlDefinitions;

const remoteModuleMap = new Map<string, unknown>();
const remoteContainerMap = new Map<string, unknown>();

export const getRemoteModuleMap = () => remoteModuleMap;
export const getRemoteContainerMap = () => remoteContainerMap;

const getErrorMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error));

const normalizeError = (error: unknown): Error => {
    if (error instanceof Error) return error;
    const normalized = new Error(getErrorMessage(error));
    (normalized as Error & { cause?: unknown }).cause = error;
    return normalized;
};

const getNow = (): number => performance.now();

const getDurationMs = (start: number): number => Math.round(getNow() - start);

export async function loadManifest() {
    const environment = offerSessionUtils.getEnvironmentType();
    const baseURL = environment === 'prod' ? 'https://pipelines.app.coxautoinc.com' : 'https://np-pipelines.app.coxautoinc.com'; // "http://localhost:4200" for full local dev experience with local crw
    const moduleFederationManifest = baseURL + '/module-federation.manifest.json';
    try {
        const manifest = await fetch(moduleFederationManifest);
        return await manifest.json();
    } catch (error) {
        logNewRelicPageAction(MODULE_FEDERATION_NR_EVENTS.MANIFEST_LOAD_FAILURE, {
            environment,
            manifestUrl: moduleFederationManifest,
            errorMessage: getErrorMessage(error)
        });
        throw new Error('Failed to fetch remote module location manifest');
    }
}

export async function loadRemoteModule(remoteName: string, moduleName: string) {
    const start = getNow();
    const environment = offerSessionUtils.getEnvironmentType();

    try {
        if (!remoteUrlDefinitions) {
            const definitions: Record<string, string> = await loadManifest();
            setRemoteDefinitions(definitions);
        }
        const remoteModuleKey = `${remoteName}:${moduleName}`;
        if (remoteModuleMap.has(remoteModuleKey)) {
            return remoteModuleMap.get(remoteModuleKey);
        }

        const container = remoteContainerMap.has(remoteName)
            ? remoteContainerMap.get(remoteName)
            : await loadRemoteContainer(remoteName);

        const factory = await (container as any).get(moduleName);
        const Module = factory();

        remoteModuleMap.set(remoteModuleKey, Module);

        logNewRelicPageAction(MODULE_FEDERATION_NR_EVENTS.MODULE_LOAD_SUCCESS, {
            environment,
            remoteName,
            moduleName,
            durationMs: getDurationMs(start)
        });

        return Module;
    } catch (error) {
        logNewRelicPageAction(MODULE_FEDERATION_NR_EVENTS.MODULE_LOAD_FAILURE, {
            environment,
            remoteName,
            moduleName,
            durationMs: getDurationMs(start),
            errorMessage: getErrorMessage(error)
        });
        throw normalizeError(error);
    }
}

let initialSharingScopeCreated = false;

export function setInitialSharingScopeCreated(isSet: boolean) {
    initialSharingScopeCreated = isSet;
}

export async function loadRemoteContainer(remoteName: string) {
    if (!remoteUrlDefinitions) {
        throw new Error('Call setRemoteDefinitions to allow Dynamic Federation to find the remote apps correctly.');
    }

    const remoteUrl = remoteUrlDefinitions[remoteName];
    const containerUrl = `${remoteUrl}${remoteUrl.endsWith('/') ? '' : '/'}remoteEntry.js`;

    try {
        if (!initialSharingScopeCreated) {
            initialSharingScopeCreated = true;
            await __webpack_init_sharing__('default');
        }

        const container: any = await importModule(containerUrl);
        await container.init(__webpack_share_scopes__.default);

        remoteContainerMap.set(remoteName, container);
        return container;
    } catch (error) {
        logNewRelicPageAction(MODULE_FEDERATION_NR_EVENTS.REMOTE_CONTAINER_LOAD_FAILURE, {
            environment: offerSessionUtils.getEnvironmentType(),
            remoteName,
            remoteUrl: containerUrl,
            errorMessage: getErrorMessage(error)
        });
        throw normalizeError(error);
    }
}
